import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, 
  Mail, 
  Phone, 
  MapPin,
  MessageSquare,
  Send,
  ArrowLeft,
  Clock,
  HelpCircle,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success('Message sent! We\'ll get back to you within 24-48 hours.');
    setFormData({ name: '', email: '', subject: 'general', message: '' });
    setSubmitting(false);
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email Us",
      description: "For anything and everything",
      value: "hello@reddi.com",
      action: "mailto:hello@reddi.com"
    },
    {
      icon: Phone,
      title: "Call Us",
      description: "We actually answer",
      value: "1-800-REDDI-NOW",
      action: "tel:1-800-733-3466"
    },
    {
      icon: MessageSquare,
      title: "Live Chat",
      description: "Quick answers in the app",
      value: "Open the app",
      action: null
    }
  ];

  const departments = [
    { value: "general", label: "General Question" },
    { value: "rider-support", label: "Rider Support" },
    { value: "driver-support", label: "Driver Support" },
    { value: "partnerships", label: "Partnerships" },
    { value: "press", label: "Press & Media" },
    { value: "careers", label: "Want to Work Here?" },
    { value: "safety", label: "Safety Concern" },
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-['Barlow_Condensed'] font-black text-white uppercase mb-6">
            Let's Talk
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Got questions? Feedback? Just want to say hi? We're real humans who actually read these. 
            Hit us up.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {contactMethods.map((method, index) => (
            <a
              key={index}
              href={method.action || '#'}
              className="p-6 bg-slate-800/50 border border-slate-700 text-center hover:border-red-500/50 transition-colors block"
            >
              <method.icon className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-1">{method.title}</h3>
              <p className="text-slate-400 text-sm mb-2">{method.description}</p>
              <p className="text-red-400 font-medium">{method.value}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <Send className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">
              Drop Us a Line
            </h2>
            <p className="text-slate-400">Fill this out and we'll get back to you. Promise.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Your Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="What should we call you?"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="you@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">What's this about?</label>
              <select
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                {departments.map((dept) => (
                  <option key={dept.value} value={dept.value}>{dept.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Your Message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
                rows={6}
                placeholder="What's on your mind?"
                required
              />
            </div>

            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
            >
              {submitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </div>
      </section>

      {/* Response Time */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-4 p-6 bg-slate-800/30 border border-slate-700">
            <Clock className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-white font-semibold">We typically respond within 24-48 hours</p>
              <p className="text-slate-400">Business hours, but we try to be quick</p>
            </div>
          </div>
        </div>
      </section>

      {/* Office - simplified for startup */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <MapPin className="w-10 h-10 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">
            Where We're At
          </h2>
          <p className="text-slate-400 mb-8">
            We're a distributed team working from wherever the WiFi is good. 
            Headquarters vibes coming soon as we grow.
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-red-600/20 border border-red-600/30 rounded-lg">
            <Heart className="w-5 h-5 text-red-500" />
            <span className="text-red-400 font-medium">Remote-first, human-always</span>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <HelpCircle className="w-12 h-12 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">
            Need Quick Answers?
          </h2>
          <p className="text-slate-400 mb-8">
            Check our Help Center—you might find what you need faster.
          </p>
          <Link to="/help">
            <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg">
              Visit Help Center
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          © 2025 REDDI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Contact;
